/**
 * The box you write a support message in. One of them, everywhere.
 *
 * There were three: the new-report composer, the reply in the bubble, and the
 * reply on the staff thread. They had drifted into three different widgets —
 * different borders, different places for the pictures, different ways to add
 * one, and Ctrl+V working in some of them and not others. Nobody can learn a
 * widget that changes shape depending on which support box they are looking
 * at, so this is the only one.
 *
 * What never varies: the whole thing is a single bordered object, pictures
 * waiting to be sent stand IN the text where they will appear
 * (`support-composer-editor`), a paperclip offers the capture sources, and
 * pasting an image attaches it at the caret.
 *
 * What varies is `layout`, and only because the field's height forces it. A
 * one-line box takes its controls beside it, the way WhatsApp and iMessage
 * do. A box that starts multi-line and grows takes a toolbar underneath, the
 * way Slack and Teams do — controls beside a growing field either stretch
 * into tall columns or drift away from its baseline.
 */

import SendIcon from "@mui/icons-material/Send";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import SupportComposerEditor, { type ComposerCaret } from "./support-composer-editor";
import type { DraftPicture } from "./support-draft-pictures";
import { hasWrittenText, splitPictureTokens } from "./support-picture-tokens";
import {
	type ReplyPictureSources,
	SupportAttachButton,
	SupportReplyPicture,
} from "./support-reply-pictures";
import SupportSendLater, { type SendLater } from "./support-send-later";

interface SupportComposerProps {
	value: string;
	onChange: (value: string) => void;
	placeholder: string;
	onSend: () => void;
	isSending: boolean;

	pictures: DraftPicture[];
	/** A picture arriving from the paperclip or from a paste. */
	onAddPicture: (blob: Blob) => void;
	onEditPicture: (key: number) => void;
	onRemovePicture: (key: number) => void;
	sources: ReplyPictureSources;

	/** Follows the field's height, never taste. See the module docstring. */
	layout: "inline" | "stacked";
	/** Only meaningful when stacked; an inline field is one line by definition. */
	minRows?: number;
	/** The bubble is a column ~360px wide, so its thumbnails are smaller. */
	compact?: boolean;
	sendLabel?: string;
	/** Staff reply only: turns SEND into a split button with Send later. */
	sendLater?: SendLater;
	/** Distinguishes the three in tests and in the DOM. */
	dataComponent: string;
	autoFocus?: boolean;
	/** The caret, for a caller that writes at it. */
	inputRef?: React.Ref<ComposerCaret>;
}

const SupportComposer: React.FC<SupportComposerProps> = ({
	value,
	onChange,
	placeholder,
	onSend,
	isSending,
	pictures,
	onAddPicture,
	onEditPicture,
	onRemovePicture,
	sources,
	layout,
	minRows = 2,
	compact = false,
	sendLabel = "Send",
	sendLater,
	dataComponent,
	autoFocus = false,
	inputRef,
}) => {
	const attach = <SupportAttachButton onAdd={onAddPicture} sources={sources} />;
	const send = sendLater ? (
		<SupportSendLater
			sendLater={sendLater}
			sendLabel={sendLabel}
			sendDisabled={isSending || !hasWrittenText(value)}
			onSend={onSend}
			dataComponent={dataComponent}
		/>
	) : (
		<Button
			variant="contained"
			size="small"
			startIcon={<SendIcon />}
			disabled={isSending || !hasWrittenText(value)}
			onClick={onSend}
			data-component={`${dataComponent}-send`}
		>
			{sendLabel}
		</Button>
	);
	const input = (
		<SupportComposerEditor
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			pictures={pictures}
			onAddPicture={onAddPicture}
			onEditPicture={onEditPicture}
			onRemovePicture={onRemovePicture}
			caretRef={inputRef}
			compact={compact}
			autoFocus={autoFocus}
			minRows={layout === "inline" ? 1 : minRows}
			maxRows={layout === "inline" ? 6 : undefined}
			px={layout === "inline" ? 0 : 1.5}
			py={layout === "inline" ? 0.5 : 1.25}
			dataComponent={dataComponent}
		/>
	);
	// A picture no line names follows the text, as it will in the sent
	// message — shown there rather than not at all.
	const placed = new Set(
		splitPictureTokens(value).flatMap((part) =>
			part.kind === "picture" ? [part.number] : []
		)
	);
	const trailing = pictures.filter((_, index) => !placed.has(index + 1));
	const unplaced = trailing.length > 0 && (
		<Stack
			direction="row"
			spacing={1}
			flexWrap="wrap"
			useFlexGap
			sx={{ px: compact ? 1 : 1.5, pb: 1 }}
			data-component={`${dataComponent}-unplaced-pictures`}
		>
			{trailing.map((picture) => (
				<SupportReplyPicture
					key={picture.key}
					picture={picture}
					onEdit={onEditPicture}
					onRemove={onRemovePicture}
					compact={compact}
				/>
			))}
		</Stack>
	);

	return (
		<Paper
			variant="outlined"
			sx={{
				borderRadius: 1,
				// The container takes the focus ring the input gave up, so
				// clicking anywhere in it lights the whole message, not an
				// inner box inside an outer one.
				"&:focus-within": {
					borderColor: "primary.main",
					boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
				},
			}}
			data-component={dataComponent}
		>
			{layout === "inline" ? (
				<Stack
					direction="row"
					spacing={0.5}
					alignItems="center"
					sx={{ p: 0.5 }}
				>
					{attach}
					{input}
					{send}
				</Stack>
			) : (
				<>
					{input}
					{unplaced}
					{/* px matches the input's, so the send button's right edge and
					    the text's right edge are the same line. At px 1 against
					    the input's 1.5 the controls hung four pixels further out
					    than the text they belong to, and nothing aligned. */}
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						sx={{ px: 1.5, pb: 1 }}
					>
						{attach}
						{send}
					</Stack>
				</>
			)}
			{layout === "inline" && unplaced}
		</Paper>
	);
};

export default SupportComposer;
